import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, BadgeCheck, Check, Copy, MessageCircle, Upload, X } from "lucide-react";
import { API, QR_CODE_URL, track, whatsappLink } from "../lib/site";
import { useSite } from "../context/SiteContext";

export const BookingModal = ({ payload, onClose }) => {
  const { rooms, settings } = useSite();
  const prefillNotes = payload.offer ? `Offer enquiry: ${payload.offer.name}` : "";
  const [form, setForm] = useState({
    room_type: payload.room?.name || payload.prefill?.room || "",
    guest_name: "", email: "", phone: "",
    check_in: payload.prefill?.check_in || "",
    check_out: payload.prefill?.check_out || "",
    guests: payload.prefill?.guests || 2,
    notes: prefillNotes,
  });
  const [sending, setSending] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [utr, setUtr] = useState("");
  const [proof, setProof] = useState(null);
  const [utrSubmitting, setUtrSubmitting] = useState(false);
  const [utrDone, setUtrDone] = useState(false);
  const [cashMode, setCashMode] = useState(false);
  const [cashConfirming, setCashConfirming] = useState(false);
  const [utrError, setUtrError] = useState("");
  const [method, setMethod] = useState(null);
  const [upiCopied, setUpiCopied] = useState(false);
  const update = (key) => (event) => setForm({ ...form, [key]: event.target.value });

  const copyUpiId = async () => {
    try {
      await navigator.clipboard.writeText(settings.upi_id || "");
      setUpiCopied(true);
      toast.success("UPI ID copied");
      setTimeout(() => setUpiCopied(false), 2000);
    } catch {
      toast.info(`Pay to UPI ID: ${settings.upi_id || ""}`);
    }
  };

  const bookedRoom = rooms.find((r) => r.name === confirmation?.room_type);
  const nights = confirmation ? Math.round((new Date(confirmation.check_out) - new Date(confirmation.check_in)) / 86400000) : 0;
  const rateNum = bookedRoom ? parseInt(bookedRoom.rate.replace(/[^0-9]/g, ""), 10) : 0;
  const estimatedTotal = nights > 0 && rateNum ? nights * rateNum : null;

  const methods = [
    settings.pay_upi !== false && { id: "upi", label: "UPI / QR" },
    settings.pay_bank !== false && { id: "bank", label: "Bank transfer" },
    settings.pay_cash !== false && { id: "cash", label: "Cash at hotel" },
  ].filter(Boolean);
  const activeMethod = methods.find((m) => m.id === method) ? method : methods[0]?.id;

  const bankRows = [
    { label: "Account name", value: settings.account_name || "Madhubhan Resort & Spa" },
    { label: "Bank", value: settings.bank_name || "Indian Bank" },
    { label: "Account number", value: settings.account_number || "8390476916" },
    { label: "IFSC code", value: settings.ifsc || "IDIB000A021" },
    { label: "Branch", value: settings.bank_branch || "Anand" },
    { label: "Account type", value: settings.account_type || "Current" },
  ];
  const qrSrc = settings.qr_image || QR_CODE_URL;

  const submit = async (event) => {
    event.preventDefault();
    setSending(true);
    try {
      const response = await axios.post(`${API}/bookings`, { ...form, guests: Number(form.guests) });
      track("booking_request_submitted", { room: form.room_type, booking_id: response.data.booking_id });
      setConfirmation(response.data);
    } catch (error) {
      toast.error("We could not send your request", { description: "Please call or WhatsApp our reservations desk." });
    } finally { setSending(false); }
  };

  const submitUtr = async (event) => {
    event.preventDefault();
    if (!proof) {
      setUtrError("Please attach your payment screenshot — it is required to confirm the booking.");
      return;
    }
    if (utr.trim().length < 6) {
      setUtrError("Please enter the full UTR / transaction reference number from your payment app.");
      return;
    }
    setUtrSubmitting(true);
    setUtrError("");
    try {
      const body = new FormData();
      body.append("file", proof);
      await axios.put(`${API}/bookings/${confirmation.booking_id}/method`, { payment_method: activeMethod });
      await axios.post(`${API}/bookings/${confirmation.booking_id}/payment-proof`, body);
      await axios.put(`${API}/bookings/${confirmation.booking_id}/utr`, { utr: utr.trim() });
      track("utr_submitted", { booking_id: confirmation.booking_id, method: activeMethod });
      setUtrDone(true);
    } catch (err) {
      setUtrError(err.response?.data?.detail || "Could not submit. Please check the screenshot and UTR, then try again.");
    } finally { setUtrSubmitting(false); }
  };

  const confirmCash = async () => {
    setCashConfirming(true);
    try {
      await axios.put(`${API}/bookings/${confirmation.booking_id}/method`, { payment_method: "cash" });
      track("booking_cash_selected", { booking_id: confirmation.booking_id });
      setCashMode(true);
      setUtrDone(true);
    } catch (err) {
      toast.error("Could not confirm — please try again.");
    } finally { setCashConfirming(false); }
  };

  return (
    <div className="modal-backdrop" data-testid="booking-modal">
      <div className="booking-modal" role="dialog" aria-modal="true" data-testid="booking-dialog">
        <button className="close-button" onClick={onClose} aria-label="Close booking form" data-testid="booking-modal-close"><X size={20} /></button>
        {confirmation ? (
          utrDone ? (
            <div className="confirmation" data-testid="booking-confirmation">
              <BadgeCheck size={44} className="confirmation-icon" />
              <span className="eyebrow">{cashMode ? "Booking noted" : "Payment reference received"}</span>
              <h2>You're all<br /><em>set.</em></h2>
              <p className="muted">
                Booking <strong data-testid="booking-reference">{confirmation.booking_id}</strong>
                {cashMode
                  ? <> — pay <strong>₹{(estimatedTotal || 0).toLocaleString("en-IN")}</strong> in cash at check-in. Our desk will call or WhatsApp you to confirm.</>
                  : <> · UTR <strong data-testid="utr-confirm-value">{utr}</strong> · screenshot attached. Our reservations desk will verify your payment and confirm {confirmation.room_type || "your stay"} shortly.</>}
              </p>
              {settings.whatsapp_booking !== false && (
                <a
                  className="gold-button full-width"
                  href={whatsappLink(`Hello Madhubhan Resort & Spa, booking ${confirmation.booking_id}${cashMode ? " — cash at check-in" : ` — UTR ${utr}, screenshot submitted`}.`, settings.whatsapp)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => track("whatsapp_click", { location: "booking_confirmation" })}
                  data-testid="confirmation-whatsapp"
                >
                  <MessageCircle size={16} /> Continue on WhatsApp
                </a>
              )}
            </div>
          ) : (
            <div className="confirmation" data-testid="payment-step">
              <span className="eyebrow">Step 2 — Payment</span>
              <h2>Complete payment<br /><em>to confirm.</em></h2>
              <p className="muted">
                Reference <strong data-testid="booking-reference">{confirmation.booking_id}</strong> — {confirmation.room_type || "your stay"},
                {" "}{confirmation.check_in} to {confirmation.check_out}. Your booking is <strong>not confirmed yet</strong> — it completes only after payment.
              </p>
              {estimatedTotal && (
                <div className="amount-banner" data-testid="payment-amount">
                  <span>Total payable to confirm your booking</span>
                  <strong data-testid="payment-total">₹{estimatedTotal.toLocaleString("en-IN")}</strong>
                  <small>{nights} night{nights > 1 ? "s" : ""} × {bookedRoom.rate} · taxes & breakfast included</small>
                </div>
              )}
              {methods.length > 1 && (
                <div className="method-chips" data-testid="payment-methods">
                  {methods.map((m) => (
                    <button key={m.id} type="button" className={activeMethod === m.id ? "filter-chip active" : "filter-chip"} onClick={() => setMethod(m.id)} data-testid={`method-${m.id}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
              )}
              {activeMethod === "upi" && (
                <div className="qr-block" data-testid="payment-qr">
                  <div className="qr-payee">
                    <span className="qr-pay-label">Pay to: Madhubhan Resort &amp; Spa</span>
                    <a href={qrSrc} target="_blank" rel="noreferrer" className="qr-zoom" title="Tap to open the full-size QR" data-testid="payment-qr-zoom">
                      <img
                        src={qrSrc}
                        alt="Scan to pay Madhubhan Resort & Spa by UPI"
                        onError={(e) => { if (!e.target.dataset.fallback) { e.target.dataset.fallback = "1"; e.target.src = QR_CODE_URL; } }}
                        data-testid="payment-qr-image"
                      />
                    </a>
                  </div>
                  <div className="qr-side">
                    <p>Scan with any UPI app — Paytm, PhonePe, Google Pay or BHIM — to pay the full amount instantly. Tap the QR to enlarge it.</p>
                    {settings.upi_id && (
                      <div className="upi-fallback" data-testid="upi-fallback">
                        <span>Can't scan? Pay to UPI ID instead</span>
                        <button type="button" className="upi-copy" onClick={copyUpiId} data-testid="upi-id-copy">
                          <strong>{settings.upi_id}</strong> {upiCopied ? <Check size={13} /> : <Copy size={13} />} {upiCopied ? "Copied" : "Copy"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {activeMethod === "bank" && (
                <div className="bank-card" data-testid="bank-details">
                  {bankRows.map((row) => (
                    <div key={row.label}><span>{row.label}</span><strong>{row.value}</strong></div>
                  ))}
                </div>
              )}
              {(activeMethod === "upi" || activeMethod === "bank") && (
                <form className="utr-form" onSubmit={submitUtr} data-testid="utr-form">
                  <label>UTR / transaction reference number
                    <input value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="e.g. 5283XXXXXX21" required data-testid="utr-input" />
                  </label>
                  <label className="outline-button upload-btn proof-upload" data-testid="proof-upload-label">
                    <Upload size={14} /> {proof ? proof.name : "Attach payment screenshot (required)"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => setProof(e.target.files?.[0] || null)} data-testid="proof-upload" />
                  </label>
                  {utrError && <p className="utr-error" data-testid="utr-error">{utrError}</p>}
                  <button className="primary-button full-width" disabled={utrSubmitting} type="submit" data-testid="utr-submit">
                    {utrSubmitting ? "Submitting…" : "Submit UTR + screenshot & confirm"} <ArrowUpRight size={16} />
                  </button>
                </form>
              )}
              {activeMethod === "cash" && (
                <div data-testid="cash-panel">
                  <p className="muted">Pay <strong>₹{(estimatedTotal || 0).toLocaleString("en-IN") || "the stay total"}</strong> in cash when you check in. Your room will be held under your name.</p>
                  <button className="primary-button full-width" onClick={confirmCash} disabled={cashConfirming} data-testid="cash-confirm">
                    {cashConfirming ? "Confirming…" : "Confirm booking — pay cash at hotel"} <ArrowUpRight size={16} />
                  </button>
                </div>
              )}
              <p className="muted small" data-testid="payment-instructions">{settings.payment_instructions || "Submit payment details to confirm — you will only be connected to WhatsApp afterwards."}</p>
            </div>
          )
        ) : (
          <>
            <span className="eyebrow">Private reservations desk</span>
            <h2>Plan your<br /><em>stay.</em></h2>
            <p className="muted" data-testid="booking-availability-note">
              {payload.prefill
                ? <>Good news — <strong>every stay is available</strong> for {form.check_in} to {form.check_out}. Share your details and we will confirm your rate.</>
                : "Every room is available on request — tell us your dates and we will confirm your rate and payment instructions."}
            </p>
            <form onSubmit={submit} data-testid="booking-form">
              <label>Room or suite
                <select value={form.room_type} onChange={update("room_type")} required data-testid="booking-room-select">
                  <option value="">Choose a stay</option>
                  {rooms.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
                </select>
              </label>
              <div className="form-grid">
                <label>Check in<input type="date" value={form.check_in} onChange={update("check_in")} required data-testid="booking-check-in" /></label>
                <label>Check out<input type="date" value={form.check_out} onChange={update("check_out")} required data-testid="booking-check-out" /></label>
              </div>
              <div className="form-grid">
                <label>Your name<input value={form.guest_name} onChange={update("guest_name")} required placeholder="Full name" data-testid="booking-name" /></label>
                <label>Guests
                  <select value={form.guests} onChange={update("guests")} data-testid="booking-guests">
                    {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
              </div>
              <div className="form-grid">
                <label>Email<input type="email" value={form.email} onChange={update("email")} required placeholder="you@email.com" data-testid="booking-email" /></label>
                <label>Phone<input type="tel" value={form.phone} onChange={update("phone")} required placeholder="Mobile number" data-testid="booking-phone" /></label>
              </div>
              <label>Anything we should prepare?
                <textarea value={form.notes} onChange={update("notes")} placeholder="Celebration, dietary needs, arrival time…" data-testid="booking-notes" />
              </label>
              <button className="primary-button full-width" disabled={sending} type="submit" data-testid="booking-submit">
                {sending ? "Sending request…" : "Request availability"} <ArrowUpRight size={16} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
